import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Send, LifeBuoy, Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const SUPPORT_EMAIL = "supportmairuncoach@gmail.com";
const GREETING =
  "Bonjour ! Je suis l'assistant de support MAIRUNCOACH. Je peux t'aider sur le fonctionnement de l'app : bilan, VMA, programme, offres & paiement, parrainage, rendez-vous coach, évolution… Pose-moi ta question.";

export default function Support() {
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showMail, setShowMail] = useState(false);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    const history = messages.filter((m) => m.role === "user" || m.role === "assistant");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/support/chat", { message: text, history });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.reply && data.reply.includes(SUPPORT_EMAIL)) setShowMail(true);
    } catch {
      toast.error("Erreur de connexion");
      setMessages((m) => [...m, { role: "assistant", content: `Je rencontre un souci technique. Écris-nous à ${SUPPORT_EMAIL} et l'équipe te répondra rapidement.` }]);
      setShowMail(true);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-sand flex flex-col h-screen [height:100dvh] overflow-hidden" data-testid="support-page">
      <header className="shrink-0 sticky top-0 z-30 bg-sand/90 backdrop-blur-md border-b border-border">
        <div className="max-w-2xl mx-auto px-5 h-16 flex items-center justify-between">
          <button data-testid="support-back" onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-secondary transition-[background-color]"><ArrowLeft size={20} strokeWidth={1.5} /></button>
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-sage/15 flex items-center justify-center"><LifeBuoy size={16} className="text-sage" /></span>
            <div className="text-sm font-medium">Assistant support</div>
          </div>
          <div className="w-8" />
        </div>
      </header>

      <div className="flex-1 min-h-0 w-full max-w-2xl mx-auto px-5 flex flex-col">
        <div ref={scrollRef} data-testid="support-messages" className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-6 space-y-5">
          <div className="text-[11px] text-obsidian/40 text-center">Tu échanges avec une IA. Pour toute autre demande, contacte {SUPPORT_EMAIL}.</div>
          {messages.map((m, i) => (
            <div key={i} className="fade-up">
              {m.role === "assistant" ? (
                <div className="flex gap-3">
                  <span className="mt-1 w-7 h-7 shrink-0 rounded-full bg-sage/15 flex items-center justify-center"><LifeBuoy size={13} className="text-sage" /></span>
                  <div className="rounded-2xl rounded-tl-sm bg-white border border-border px-4 py-3 text-[15px] leading-relaxed text-obsidian/90 max-w-[88%]">{m.content}</div>
                </div>
              ) : (
                <div className="flex justify-end"><div className="rounded-2xl rounded-tr-sm bg-obsidian text-white px-4 py-3 text-[15px] leading-relaxed max-w-[85%]">{m.content}</div></div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex gap-3">
              <span className="mt-1 w-7 h-7 shrink-0 rounded-full bg-sage/15 flex items-center justify-center"><LifeBuoy size={13} className="text-sage" /></span>
              <div className="rounded-2xl rounded-tl-sm bg-white border border-border px-4 py-3"><span className="pulse-organic">…</span></div>
            </div>
          )}
          {showMail && (
            <div className="flex justify-center">
              <a data-testid="support-mail-cta" href={`mailto:${SUPPORT_EMAIL}`}
                className="inline-flex items-center gap-2 rounded-full bg-sage text-white text-sm font-semibold px-4 py-2.5 hover:-translate-y-0.5 transition-transform">
                <Mail size={15} /> Écrire à {SUPPORT_EMAIL}
              </a>
            </div>
          )}
        </div>

        <div className="flex items-end gap-3 pt-3 pb-4 border-t border-border shrink-0">
          <textarea data-testid="support-input" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1} placeholder="Pose ta question sur l'app…"
            className="flex-1 resize-none rounded-2xl border border-border bg-white px-4 py-3 text-[15px] focus:ring-1 focus:ring-obsidian focus:outline-none" />
          <button data-testid="support-send" onClick={send} disabled={loading || !input.trim()}
            className="w-11 h-11 shrink-0 rounded-full bg-obsidian text-white flex items-center justify-center disabled:opacity-30 hover:-translate-y-0.5 transition-transform"><Send size={17} /></button>
        </div>
      </div>
    </div>
  );
}
